import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";

const links = [
  { to: "/", key: "nav.home" },
  { to: "/bowling", key: "nav.bowling" },
  { to: "/arrangementen", key: "nav.arrangements" },
  { to: "/kinderfeestjes", key: "nav.kids" },
  { to: "/restaurant", key: "nav.restaurant" },
  { to: "/jenever-discobowling", key: "nav.jenever" },
  { to: "/contact", key: "nav.contact" },
  { to: "/faq", key: "nav.faq" },
];

export const Navbar = () => {
  const { t, lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "glass py-3" : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="font-brand text-xl neon tracking-[0.25em] uppercase">
            HELIOS
          </Link>

          <nav className="hidden xl:flex items-center gap-6">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.key.split(".")[1]}`}
                className={({ isActive }) =>
                  `text-[13px] font-semibold uppercase tracking-wider transition-colors ${
                    isActive ? "text-[#00F0FF]" : "text-[#9db8d6] hover:text-[#E2F1FF]"
                  }`
                }
              >
                {t(l.key)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="lang-toggle-button"
              onClick={() => setLang(lang === "nl" ? "en" : "nl")}
              className="flex items-center gap-2 px-3 py-2 rounded-full border border-[#00F0FF]/40 text-xs font-bold tracking-widest text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors"
            >
              <Globe size={14} />
              {lang === "nl" ? "NL" : "EN"}
            </button>
            <Link
              to="/reserveer"
              data-testid="nav-book-cta"
              className="hidden sm:inline-flex btn-neon font-display text-sm tracking-[0.2em] uppercase px-6 py-2.5 rounded-full"
            >
              {t("nav.book")}
            </Link>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="xl:hidden text-[#E2F1FF] p-2"
              aria-label="Menu"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[#05070B]/95 backdrop-blur-xl xl:hidden"
          >
            <div className="flex flex-col items-center justify-center h-full gap-5">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.key.split(".")[1]}`}
                    className="font-display text-3xl uppercase tracking-wider text-[#E2F1FF] hover:text-[#00F0FF]"
                  >
                    {t(l.key)}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
                <Link
                  to="/reserveer"
                  data-testid="mobile-book-cta"
                  className="btn-neon font-display text-lg tracking-[0.2em] uppercase px-10 py-4 rounded-full mt-4 inline-block"
                >
                  {t("nav.book")}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
